<template>
  <div class="mini-tab-content">
    <div
      v-if="loading"
      class="interc-loading"
    >
      <b-spinner
        small
        variant="primary"
      />
      <span>Carregando...</span>
    </div>

    <template v-else>
      <div class="interc-toolbar">
        <span class="interc-toolbar__title">
          <feather-icon
            icon="ActivityIcon"
            size="13"
            class="mr-25 text-primary"
          />
          {{ assessments.length ? assessments.length + ' avaliação(ões)' : 'Nenhuma avaliação' }}
        </span>
        <button
          class="btn btn-sm btn-primary"
          @click="openModal"
        >
          <feather-icon
            icon="PlusIcon"
            size="13"
            class="mr-25"
          />
          Nova Avaliação
        </button>
      </div>

      <div
        v-if="!assessments.length"
        class="interc-empty"
      >
        <feather-icon
          icon="ActivityIcon"
          size="26"
        />
        <p>Nenhuma avaliação neurológica registrada.</p>
      </div>

      <!-- Histórico -->
      <div
        v-if="assessments.length"
        class="gcs-history"
      >
        <div
          v-for="a in assessments"
          :key="a.id"
          :class="['gcs-card', { 'gcs-card--cancelled': a.cancelled_by_user_id }]"
        >
          <div
            class="gcs-card__score"
            :class="scoreClass(a)"
          >
            <span class="gcs-card__score-value">{{ a.cancelled_by_user_id ? '—' : displayTotal(a) }}</span>
            <span class="gcs-card__score-label">{{ severityLabel(a.severity) }}</span>
          </div>

          <div class="gcs-card__body">
            <div class="gcs-card__chips">
              <span class="gcs-chip"><strong>O</strong> {{ componentDisplay(a.eye_opening, a.eye_not_testable) }}</span>
              <span class="gcs-chip"><strong>V</strong> {{ componentDisplay(a.verbal_response, a.verbal_not_testable) }}</span>
              <span class="gcs-chip"><strong>M</strong> {{ componentDisplay(a.motor_response, a.motor_not_testable) }}</span>
              <button
                v-if="!a.cancelled_by_user_id"
                class="gcs-card__del"
                title="Cancelar registro"
                @click="cancelAssessment(a.id)"
              >
                <feather-icon
                  icon="Trash2Icon"
                  size="13"
                />
              </button>
            </div>

            <p
              v-if="a.notes"
              class="gcs-card__notes"
            >
              {{ a.notes }}
            </p>

            <div class="interc-item__meta">
              <feather-icon
                icon="UserIcon"
                size="11"
              />
              <span>{{ a.user ? a.user.name : '—' }}</span>
              <span class="interc-item__dot" />
              <feather-icon
                icon="ClockIcon"
                size="11"
              />
              <span>{{ a.created_at | formatDate }}</span>
            </div>

            <div
              v-if="a.cancelled_by_user_id"
              class="interc-item__cancelled-row"
            >
              <feather-icon
                icon="XCircleIcon"
                size="11"
                class="mr-25"
              />
              Cancelado por
              <strong class="ml-25">{{ a.cancelled_by ? a.cancelled_by.name : '—' }}</strong>
              · {{ a.cancelled_at | formatDate }}
            </div>
          </div>
        </div>
      </div>
    </template>

    <!-- Modal de nova avaliação -->
    <b-modal
      v-model="showModal"
      title="Nova Avaliação Neurológica — Escala de Glasgow"
      :title-class="'font-weight-bold'"
      size="xl"
      centered
      no-close-on-backdrop
      @hidden="resetForm"
    >
      <!-- Barra de total ao vivo -->
      <div
        class="gcs-live"
        :class="liveScoreClass"
      >
        <div class="gcs-live__total">
          <span class="gcs-live__total-value">{{ liveTotalDisplay }}</span>
          <span class="gcs-live__total-caption">{{ liveTotalCaption }}</span>
        </div>
        <div class="gcs-live__breakdown">
          <span class="gcs-live__bd">
            <small>Ocular</small><strong>{{ liveComponent('eye') }}</strong>
          </span>
          <span class="gcs-live__op">+</span>
          <span class="gcs-live__bd">
            <small>Verbal</small><strong>{{ liveComponent('verbal') }}</strong>
          </span>
          <span class="gcs-live__op">+</span>
          <span class="gcs-live__bd">
            <small>Motora</small><strong>{{ liveComponent('motor') }}</strong>
          </span>
        </div>
        <div class="gcs-live__severity">
          {{ liveSeverityLabel }}
        </div>
      </div>

      <!-- Três componentes -->
      <div class="row gcs-components">
        <div
          v-for="comp in components"
          :key="comp.key"
          class="col-lg-4 col-md-6"
        >
          <div class="gcs-comp">
            <div class="gcs-comp__head">
              <feather-icon
                :icon="comp.icon"
                size="15"
                class="mr-50"
              />
              {{ comp.label }}
            </div>

            <button
              v-for="opt in comp.options"
              :key="opt.value"
              type="button"
              class="gcs-opt"
              :class="{ 'gcs-opt--active': !form[comp.key].nt && form[comp.key].value === opt.value }"
              @click="selectOption(comp.key, opt.value)"
            >
              <span class="gcs-opt__pts">{{ opt.value }}</span>
              <span class="gcs-opt__text">
                <span class="gcs-opt__label">{{ opt.label }}</span>
                <span class="gcs-opt__hint">{{ opt.hint }}</span>
              </span>
            </button>

            <button
              type="button"
              class="gcs-nt"
              :class="{ 'gcs-nt--active': form[comp.key].nt }"
              @click="toggleNt(comp.key)"
            >
              <feather-icon
                :icon="form[comp.key].nt ? 'CheckSquareIcon' : 'SquareIcon'"
                size="13"
                class="mr-50"
              />
              Não testável (NT)
            </button>
          </div>
        </div>
      </div>

      <div class="form-group mt-1 mb-0">
        <label class="interc-label">Observações</label>
        <textarea
          v-model="form.notes"
          class="form-control"
          rows="2"
          placeholder="Contexto da avaliação (sedação, intubação, etc.)..."
          maxlength="2000"
        />
      </div>

      <template #modal-footer>
        <span
          v-if="!isComplete"
          class="gcs-footer-hint mr-auto"
        >
          <feather-icon
            icon="AlertCircleIcon"
            size="13"
            class="mr-25"
          />
          Defina os 3 componentes (ou marque NT).
        </span>
        <button
          class="btn btn-sm btn-outline-secondary mr-1"
          @click="showModal = false"
        >
          Cancelar
        </button>
        <button
          class="btn btn-sm btn-primary"
          :disabled="saving || !isComplete"
          @click="save"
        >
          <b-spinner
            v-if="saving"
            small
            class="mr-25"
          />
          <feather-icon
            v-else
            icon="SaveIcon"
            size="13"
            class="mr-25"
          />
          Registrar Avaliação
        </button>
      </template>
    </b-modal>
  </div>
</template>

<script>
import involvedPanelMixin from './involvedPanelMixin'

const EYE_OPTIONS = [
  { value: 4, label: 'Espontânea', hint: 'Olhos abertos sem estímulo' },
  { value: 3, label: 'Ao som', hint: 'Abre ao ser chamado' },
  { value: 2, label: 'À pressão', hint: 'Abre ao estímulo doloroso' },
  { value: 1, label: 'Ausente', hint: 'Não abre os olhos' },
]
const VERBAL_OPTIONS = [
  { value: 5, label: 'Orientada', hint: 'Coerente e orientado' },
  { value: 4, label: 'Confusa', hint: 'Conversa desorientada' },
  { value: 3, label: 'Palavras inapropriadas', hint: 'Palavras soltas, sem frase' },
  { value: 2, label: 'Sons incompreensíveis', hint: 'Gemidos / sons' },
  { value: 1, label: 'Ausente', hint: 'Nenhuma resposta verbal' },
]
const MOTOR_OPTIONS = [
  { value: 6, label: 'Obedece a comandos', hint: 'Executa ordens' },
  { value: 5, label: 'Localiza estímulo', hint: 'Move em direção à dor' },
  { value: 4, label: 'Flexão normal', hint: 'Retirada à dor' },
  { value: 3, label: 'Flexão anormal', hint: 'Decorticação' },
  { value: 2, label: 'Extensão', hint: 'Descerebração' },
  { value: 1, label: 'Ausente', hint: 'Nenhuma resposta motora' },
]

const SEVERITY_META = {
  mild: { label: 'Leve', cls: 'gcs--mild' },
  moderate: { label: 'Moderado', cls: 'gcs--moderate' },
  severe: { label: 'Grave', cls: 'gcs--severe' },
}

export default {
  name: 'NeurologicalAssessmentPanel',
  mixins: [involvedPanelMixin],
  data() {
    return {
      assessments: [],
      loading: false,
      showModal: false,
      saving: false,
      form: this.emptyForm(),
      components: [
        {
          key: 'eye', label: 'Abertura Ocular', icon: 'EyeIcon', options: EYE_OPTIONS,
        },
        {
          key: 'verbal', label: 'Resposta Verbal', icon: 'MessageSquareIcon', options: VERBAL_OPTIONS,
        },
        {
          key: 'motor', label: 'Resposta Motora', icon: 'ActivityIcon', options: MOTOR_OPTIONS,
        },
      ],
    }
  },
  computed: {
    // Total ao vivo: null se algum NT ou se incompleto.
    liveTotal() {
      const e = this.form.eye
      const v = this.form.verbal
      const m = this.form.motor
      if (e.nt || v.nt || m.nt) return null
      if (e.value == null || v.value == null || m.value == null) return null
      return e.value + v.value + m.value
    },
    isComplete() {
      return ['eye', 'verbal', 'motor'].every(k => this.form[k].nt || this.form[k].value != null)
    },
    hasNt() {
      return this.form.eye.nt || this.form.verbal.nt || this.form.motor.nt
    },
    liveSeverity() {
      return this.classify(this.liveTotal)
    },
    liveScoreClass() {
      return this.liveSeverity ? SEVERITY_META[this.liveSeverity].cls : 'gcs--neutral'
    },
    liveTotalDisplay() {
      if (this.liveTotal != null) return this.liveTotal
      if (this.isComplete && this.hasNt) return 'NT'
      return '—'
    },
    liveTotalCaption() {
      if (this.liveTotal != null) return '/ 15'
      if (this.isComplete && this.hasNt) return 'não somável'
      return 'incompleto'
    },
    liveSeverityLabel() {
      if (this.liveSeverity) return SEVERITY_META[this.liveSeverity].label
      if (this.isComplete && this.hasNt) return 'Não classificável (NT)'
      return 'Selecione os componentes'
    },
  },
  mounted() {
    this.fetchAssessments()
  },
  methods: {
    emptyForm() {
      return {
        eye: { value: null, nt: false },
        verbal: { value: null, nt: false },
        motor: { value: null, nt: false },
        notes: '',
      }
    },
    classify(total) {
      if (total == null) return null
      if (total >= 13) return 'mild'
      if (total >= 9) return 'moderate'
      return 'severe'
    },
    liveComponent(key) {
      const c = this.form[key]
      if (c.nt) return 'NT'
      return c.value == null ? '—' : c.value
    },
    // ── exibição do histórico ──
    componentDisplay(value, nt) {
      if (nt) return 'NT'
      return value == null ? '—' : value
    },
    displayTotal(a) {
      return a.total_score != null ? a.total_score : 'NT'
    },
    severityLabel(severity) {
      return severity && SEVERITY_META[severity] ? SEVERITY_META[severity].label : '—'
    },
    scoreClass(a) {
      if (a.cancelled_by_user_id) return 'gcs--neutral'
      return a.severity && SEVERITY_META[a.severity] ? SEVERITY_META[a.severity].cls : 'gcs--neutral'
    },
    // ── interação do formulário ──
    openModal() {
      this.form = this.emptyForm()
      this.showModal = true
    },
    resetForm() {
      this.form = this.emptyForm()
    },
    selectOption(key, value) {
      this.form[key].value = value
      this.form[key].nt = false
    },
    toggleNt(key) {
      const nt = !this.form[key].nt
      this.form[key].nt = nt
      if (nt) this.form[key].value = null
    },
    async fetchAssessments() {
      this.loading = true
      try {
        const res = await this.$http.get(`${this.$store.state.api}occurrence_neurological_assessments`, {
          params: {
            occurrence_id: Number(this.occurrenceId),
            involved_id: this.involvedId ? Number(this.involvedId) : undefined,
          },
        })
        this.assessments = res.data.data || []
      } catch (e) {
        this.assessments = []
        this.$message('Erro', 'Não foi possível carregar as avaliações neurológicas.', 'error')
      } finally {
        this.loading = false
      }
    },
    async save() {
      if (!this.isComplete) return
      this.saving = true
      try {
        const payload = {
          occurrence_id: Number(this.occurrenceId),
          involved_id: this.involvedId ? Number(this.involvedId) : null,
          eye_opening: this.form.eye.nt ? null : this.form.eye.value,
          verbal_response: this.form.verbal.nt ? null : this.form.verbal.value,
          motor_response: this.form.motor.nt ? null : this.form.motor.value,
          eye_not_testable: this.form.eye.nt,
          verbal_not_testable: this.form.verbal.nt,
          motor_not_testable: this.form.motor.nt,
          notes: this.form.notes || null,
        }
        const res = await this.$http.post(`${this.$store.state.api}occurrence_neurological_assessments`, payload)
        const saved = res.data.data || res.data
        this.assessments.unshift(saved)
        this.showModal = false
        this.$message('Avaliação registrada', 'Avaliação neurológica salva com sucesso.', 'success')
      } catch (e) {
        const msg = e.response && e.response.data && e.response.data.message
          ? e.response.data.message
          : 'Erro ao registrar avaliação neurológica.'
        this.$message('Erro', msg, 'error')
      } finally {
        this.saving = false
      }
    },
    async cancelAssessment(id) {
      const confirmed = await this.$bvModal.msgBoxConfirm(
        'O registro ficará marcado como cancelado e será mantido no histórico. Deseja continuar?',
        {
          title: 'Cancelar avaliação', okTitle: 'Sim, cancelar', cancelTitle: 'Voltar', okVariant: 'danger', centered: true, size: 'sm',
        },
      )
      if (!confirmed) return
      try {
        const res = await this.$http.delete(`${this.$store.state.api}occurrence_neurological_assessments/${id}`)
        const updated = res.data.data || res.data
        const idx = this.assessments.findIndex(a => a.id === id)
        if (idx !== -1) this.$set(this.assessments, idx, updated)
      } catch (e) {
        this.$message('Erro', 'Erro ao cancelar avaliação.', 'error')
      }
    },
  },
}
</script>

<style scoped>
/* ── Histórico ───────────────────────────────────────────────── */
.gcs-history {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 16px;
}

.gcs-card {
  display: flex;
  gap: 12px;
  border: 1px solid #ebe9f1;
  border-radius: 8px;
  padding: 10px 12px;
  background: #fff;
}

.gcs-card--cancelled {
  opacity: 0.66;
  background: rgba(0, 0, 0, 0.03);
}

.gcs-card__score {
  flex-shrink: 0;
  width: 64px;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 6px 0;
  color: #fff;
}

.gcs-card__score-value {
  font-size: 22px;
  font-weight: 800;
  line-height: 1;
}

.gcs-card__score-label {
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  margin-top: 3px;
  opacity: 0.95;
}

.gcs-card__body {
  flex: 1;
  min-width: 0;
}

.gcs-card__chips {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 6px;
}

.gcs-chip {
  font-size: 12px;
  color: #5e5873;
  background: #f3f2f7;
  border-radius: 6px;
  padding: 2px 8px;
}

.gcs-chip strong {
  color: #7367f0;
  margin-right: 2px;
}

.gcs-card__del {
  margin-left: auto;
  background: none;
  border: none;
  padding: 2px 4px;
  color: #ea5455;
  opacity: 0.5;
  cursor: pointer;
  line-height: 1;
  transition: opacity 0.15s;
}

.gcs-card__del:hover {
  opacity: 1;
}

.gcs-card__notes {
  font-size: 12px;
  color: #6e6b7b;
  margin: 0 0 6px;
  line-height: 1.45;
  white-space: pre-wrap;
}

/* ── Cores de gravidade (compartilhadas) ─────────────────────── */
.gcs--mild { background: #28c76f; }
.gcs--moderate { background: #ff9f43; }
.gcs--severe { background: #ea5455; }
.gcs--neutral { background: #b9b9c3; }

/* ── Barra de total ao vivo ──────────────────────────────────── */
.gcs-live {
  display: flex;
  align-items: center;
  gap: 18px;
  border-radius: 10px;
  padding: 12px 18px;
  margin-bottom: 16px;
  color: #fff;
  transition: background 0.2s;
}

.gcs-live__total {
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 64px;
}

.gcs-live__total-value {
  font-size: 34px;
  font-weight: 800;
  line-height: 1;
}

.gcs-live__total-caption {
  font-size: 11px;
  opacity: 0.9;
  margin-top: 2px;
}

.gcs-live__breakdown {
  display: flex;
  align-items: center;
  gap: 10px;
}

.gcs-live__bd {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.gcs-live__bd small {
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  opacity: 0.85;
}

.gcs-live__bd strong {
  font-size: 20px;
  font-weight: 700;
  line-height: 1.1;
}

.gcs-live__op {
  font-size: 16px;
  opacity: 0.7;
}

.gcs-live__severity {
  margin-left: auto;
  font-size: 14px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

/* ── Componentes / opções ────────────────────────────────────── */
.gcs-components {
  margin-top: 0;
}

.gcs-comp {
  border: 1px solid #ebe9f1;
  border-radius: 10px;
  padding: 10px;
  margin-bottom: 12px;
  height: 100%;
}

.gcs-comp__head {
  display: flex;
  align-items: center;
  font-size: 13px;
  font-weight: 700;
  color: #5e5873;
  margin-bottom: 8px;
  padding-bottom: 6px;
  border-bottom: 1px solid #ebe9f1;
}

.gcs-opt {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  text-align: left;
  border: 1px solid #ebe9f1;
  border-radius: 8px;
  background: #fff;
  padding: 7px 9px;
  margin-bottom: 6px;
  cursor: pointer;
  transition: all 0.12s;
}

.gcs-opt:hover {
  border-color: #c3bdf7;
  background: #faf9ff;
}

.gcs-opt--active {
  border-color: #7367f0;
  background: rgba(115, 103, 240, 0.1);
  box-shadow: 0 0 0 1px #7367f0 inset;
}

.gcs-opt__pts {
  flex-shrink: 0;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: #f0eefe;
  color: #7367f0;
  font-weight: 800;
  font-size: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.gcs-opt--active .gcs-opt__pts {
  background: #7367f0;
  color: #fff;
}

.gcs-opt__text {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.gcs-opt__label {
  font-size: 12.5px;
  font-weight: 600;
  color: #5e5873;
  line-height: 1.2;
}

.gcs-opt__hint {
  font-size: 11px;
  color: #b9b9c3;
  line-height: 1.2;
}

.gcs-opt--active .gcs-opt__label {
  color: #7367f0;
}

.gcs-nt {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  border: 1px dashed #d8d6de;
  border-radius: 8px;
  background: transparent;
  color: #b9b9c3;
  font-size: 11.5px;
  font-weight: 600;
  padding: 6px;
  cursor: pointer;
  transition: all 0.12s;
}

.gcs-nt--active {
  border-style: solid;
  border-color: #6e6b7b;
  background: rgba(110, 107, 123, 0.08);
  color: #6e6b7b;
}

.gcs-footer-hint {
  display: inline-flex;
  align-items: center;
  font-size: 11.5px;
  color: #ff9f43;
}

/* ── Dark layout ─────────────────────────────────────────────── */
.dark-layout .gcs-card {
  background: #283046;
  border-color: #3b4253;
}
.dark-layout .gcs-card--cancelled { background: rgba(0, 0, 0, 0.18); }
.dark-layout .gcs-chip {
  background: #161d31;
  color: #d0d2d6;
}
.dark-layout .gcs-card__notes { color: #b4b7bd; }
.dark-layout .gcs-comp,
.dark-layout .gcs-comp__head { border-color: #3b4253; color: #d0d2d6; }
.dark-layout .gcs-opt {
  background: #283046;
  border-color: #3b4253;
}
.dark-layout .gcs-opt:hover { background: #2f3859; }
.dark-layout .gcs-opt__label { color: #d0d2d6; }
.dark-layout .gcs-opt--active {
  background: rgba(115, 103, 240, 0.18);
}
.dark-layout .gcs-opt--active .gcs-opt__label { color: #a99ff7; }
.dark-layout .gcs-opt__pts { background: #161d31; }
</style>
